/**
 * El diagnóstico de un golpe, en la pestaña Diagnóstico del panel de Word (Rais, 12-oct-2026).
 *
 * Un botón que hace de una vez el informe de lectura, el del editor, el lector beta, el de estilo, la adecuación
 * al mercado y, al final, el plan de reescritura, y deja los Word en la carpeta del libro. Lo hace el motor, en
 * segundo plano y con el texto GUARDADO del documento: aquí solo se pide y se mira cómo va.
 *
 * GASTA DINERO DEL AUTOR, y bastante. Por eso: al abrirse solo mira (no gasta ni escribe nada); pedirlo necesita
 * DOS clics, y entre uno y otro se dice que son seis informes, que puede tardar una hora o más, qué se va a hacer
 * y lo que cuesta; y solo pregunta sola al motor mientras hay un diagnóstico en marcha. No habla con Word: el
 * panel le da la carpeta y el documento.
 */
import { useCallback, useEffect, useState } from "react";
import { COMPANION } from "./companion";
import { AVISO_DE_DURACION, AVISO_EN_MARCHA, FRASE_DE_TEXTO_CAMBIADO, FRASE_SIN_GASTO, momentoDe, botonQueToca, fraseDeQueEs, porQueNo, frenoDe, rotuloDePedir, rotuloDeConfirmar,
  fraseDelTiempo, fraseDeLoQueViaja, fraseDelDinero, listaDeInformes, fraseDeAvance, avanceDe, fraseDeHecho, fraseDeAMedias, enMarcha, miles,
  fraseDeMismoTexto, avisoDeMismoTexto, fraseDelPlanVigente, avisoDePorEscenas,
  type Diagnostico as Datos, type SinDiagnostico, SIN_GENERO, llevaGenero, fraseDeLaObra, fraseDeObraCambiada } from "./diagnosticoLogica";

export type PuenteDelDiagnostico = {
  /** Qué hay hecho para este texto, qué se haría y lo que costaría. No gasta. */
  estado: (dir: string, doc: string) => Promise<Datos | SinDiagnostico>;
  /** Pide el diagnóstico. Es lo único de esta pieza que gasta. */
  pedir: (dir: string, doc: string) => Promise<unknown>;
  /** Abre en Word el diagnóstico o el plan. Qué fichero es lo sabe el motor: de aquí no sale ningún nombre. */
  abrir: (dir: string, doc: string, cual: "diagnostico" | "plan") => Promise<{ abierto: boolean; fichero: string }>;  /** Dice qué obra es (tipo y género). No gasta: guarda dos datos junto al libro. */
  obra: (dir: string, doc: string, eleccion: { tipo: string; genero: string | null; otro?: string }) => Promise<unknown>;
};

// El panel de Word se presenta siempre como tal: es lo que le abre la puerta de la licencia de Word.
const DE_WORD = { "x-hi-cliente": "word" };
const SIN_MOTOR = "No consigo hablar con HumanInk en este ordenador. Comprueba que está abierto.";
async function leerRespuesta(r: Response) {
  const d = await r.json().catch(() => null);
  if (r.ok && d) return d;
  // Sin licencia, el motor lo dice con su frase (`message`); lo demás, con la de cada puerta (`error`).
  if (r.status === 402) throw new Error(typeof d?.message === "string" ? d.message : "Tu licencia de HumanInk no está activa en este ordenador.");
  throw new Error(typeof d?.error === "string" ? d.error : d?.error?.message ?? "HumanInk no ha contestado. Comprueba que está abierto en este ordenador.");
}
/** Lo que conteste el motor, o por qué no: un motor cerrado se dice con sus palabras, no con un «Load failed». */
const alMotor = (pedido: Promise<Response>) => pedido.catch(() => { throw new Error(SIN_MOTOR); }).then(leerRespuesta);
const mandar = (ruta: string, cuerpo: object) => alMotor(fetch(`${COMPANION}${ruta}`, {
  method: "POST", headers: { "content-type": "application/json", ...DE_WORD }, body: JSON.stringify(cuerpo),
}));
export const puenteDeWord: PuenteDelDiagnostico = {
  estado: (dir, doc) => alMotor(fetch(`${COMPANION}/diagnostico/word?${new URLSearchParams({ dir, doc })}`, { headers: DE_WORD })),
  pedir: (dir, doc) => mandar("/diagnostico/word", { dir, doc, confirmar: true }),
  abrir: (dir, doc, cual) => mandar("/diagnostico/word/abrir", { dir, doc, cual }),
  obra: (dir, doc, eleccion) => mandar("/diagnostico/word/obra", { dir, doc, ...eleccion }),
};

/** Cada cuánto se pregunta por el diagnóstico EN MARCHA. Sin uno en marcha no se pregunta nada. */
const CADA = 2500;

// Los estilos son de esta pieza y cuelgan de la paleta del panel (`tema.ts`): se ven en claro y en oscuro.
// `hd-principal` es el relleno, y solo lo lleva UN botón a la vez: el del paso que toca.
export const CSS_DEL_DIAGNOSTICO = `
.hd-raiz { font-size:13px; line-height:1.45; color:var(--hi-tinta); background:var(--hi-papel); border:1px solid var(--hi-raya); border-radius:10px; padding:12px; margin:4px 0 14px; }
.hd-raiz p { margin:0 0 6px; } .hd-raiz p:last-child { margin-bottom:0; }
.hd-rotulo { font-size:11px; font-weight:800; letter-spacing:.06em; text-transform:uppercase; color:var(--hi-oficio); margin-bottom:4px; }
.hd-tenue { color:var(--hi-tenue); font-size:12px; overflow-wrap:anywhere; }
.hd-botones { display:flex; flex-wrap:wrap; gap:6px; margin-top:10px; }
.hd-boton { font:inherit; font-size:13px; font-weight:700; padding:9px 12px; border-radius:8px; border:1px solid var(--hi-humano); background:transparent; color:var(--hi-humano); cursor:pointer; }
.hd-boton:focus-visible { outline:2px solid var(--hi-humano); outline-offset:2px; }
.hd-boton:disabled { opacity:.5; cursor:not-allowed; }
.hd-principal { background:var(--hi-oficio); border-color:var(--hi-oficio); color:var(--hi-sobre-acento); font-weight:800; }
.hd-nota { border:1px solid var(--hi-aviso-raya); border-left-width:4px; border-radius:8px; padding:10px 12px; margin-top:8px; overflow-wrap:anywhere; }
.hd-nota[data-tono="bien"] { border-color:var(--hi-bien-raya); }
.hd-nota[data-tono="mal"] { border-color:var(--hi-mal-raya); }
.hd-avance { width:100%; height:8px; margin:2px 0 6px; accent-color:var(--hi-oficio); }
.hd-cuenta { border-top:1px solid var(--hi-raya); margin-top:10px; padding-top:10px; }
.hd-obra { border-top:1px solid var(--hi-raya); margin-top:10px; padding-top:10px; display:grid; gap:6px; }
.hd-obra label { display:grid; gap:2px; font-size:12px; font-weight:700; color:var(--hi-tenue); }
.hd-obra select, .hd-obra input { font:inherit; font-size:13px; padding:7px 8px; border-radius:8px; border:1px solid var(--hi-raya); background:var(--hi-fondo); color:var(--hi-tinta); min-width:0; }
.hd-obra select:focus-visible, .hd-obra input:focus-visible { outline:2px solid var(--hi-humano); outline-offset:2px; }
.hd-obra-otro { display:flex; gap:6px; } .hd-obra-otro input { flex:1; }
.hd-aviso { font-size:14px; font-weight:800; color:var(--hi-tinta); background:var(--hi-aviso-fondo); border:1px solid var(--hi-aviso-raya); border-left-width:4px; border-radius:8px; padding:10px 12px; }
.hd-lista { margin:8px 0; padding-left:22px; } .hd-lista li { margin-bottom:2px; }
`;

const SIN_GUARDAR = "Guarda el documento en este ordenador y vuelve a esta pestaña: el diagnóstico se hace con el texto guardado.";

export default function Diagnostico({ dir, doc, puente = puenteDeWord, alCorrer }: {
  dir: string; doc: string; puente?: PuenteDelDiagnostico;
  /** Se le dice al panel si hay un diagnóstico en marcha: mientras lo hay, los informes sueltos de debajo se apagan. */
  alCorrer?: (enMarcha: boolean) => void;
}) {
  const [datos, setDatos] = useState<Datos | SinDiagnostico | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  /** El primer clic está dado: se enseña qué se va a hacer y lo que cuesta, y se espera el «sí». */
  const [pedido, setPedido] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  /** Cada vez que se termina de mirar, salga bien o mal: con un diagnóstico en marcha, es lo que vuelve a armar el reloj. */
  const [vuelta, setVuelta] = useState(0);
  /** El género «otro» se describe antes de guardarlo: mientras se escribe, vive aquí. `null`: no se está escribiendo. */
  const [otro, setOtro] = useState<string | null>(null);

  const mirar = useCallback(async () => {
    if (!dir || !doc) return;
    try { setDatos(await puente.estado(dir, doc)); setFallo(null); }
    catch (e) { setFallo(e instanceof Error ? e.message : String(e)); }
    finally { setVuelta((v) => v + 1); }
  }, [dir, doc, puente]);

  useEffect(() => {
    void mirar();
    const alVolver = () => void mirar();
    window.addEventListener("focus", alVolver);
    return () => window.removeEventListener("focus", alVolver);
  }, [mirar]);

  // Solo mientras hay un diagnóstico en marcha: se pregunta cómo va, y se deja de preguntar en cuanto acaba.
  const trabajando = enMarcha(datos?.trabajo);
  useEffect(() => {
    if (!trabajando) return;
    const reloj = setTimeout(() => void mirar(), CADA);
    return () => clearTimeout(reloj);
    // `vuelta`, y no `datos`: una consulta que falla no cambia los datos, y el avance se quedaba parado hasta volver a la ventana.
  }, [trabajando, vuelta, mirar]);
  useEffect(() => { alCorrer?.(trabajando); }, [trabajando, alCorrer]);

  const hacer = async (que: () => Promise<unknown>) => {
    if (ocupado) return;
    setOcupado(true); setFallo(null);
    try { await que(); await mirar(); }
    // Si algo falla se mira otra vez antes de decirlo: lo que haya cambiado tiene que verse ya.
    catch (e) { await mirar(); setFallo(e instanceof Error ? e.message : String(e)); }
    finally { setOcupado(false); setPedido(false); }
  };
  /** El PRIMER clic: no pide nada. Enseña la cuenta (y la mira otra vez, por si has guardado desde que se abrió la pestaña). */
  const verLaCuenta = () => { setPedido(true); void mirar(); };
  /** El SEGUNDO clic: aquí, y solo aquí, se pide el diagnóstico. */
  const confirmar = () => void hacer(() => puente.pedir(dir, doc));
  /** Decir qué obra es. No gasta: el motor guarda la elección junto al libro y la cuenta se vuelve a hacer. */
  const decirObra = (eleccion: { tipo: string; genero: string | null; otro?: string }) => void hacer(async () => { await puente.obra(dir, doc, eleccion); setOtro(null); });
  const abrir = (cual: "diagnostico" | "plan") => void hacer(async () => {
    const r = await puente.abrir(dir, doc, cual);
    if (!r.abierto) throw new Error(`No he podido abrir «${r.fichero}». Está en la carpeta del libro, junto a tu manuscrito.`);
  });

  const marco = (dentro: React.ReactNode) => (
    <section className="hd-raiz" aria-label="El diagnóstico completo">
      <style>{CSS_DEL_DIAGNOSTICO}</style>
      <div className="hd-rotulo">El diagnóstico completo</div>
      {dentro}
    </section>
  );
  if (!doc) return marco(<p>{SIN_GUARDAR}</p>);
  if (!datos) return marco(fallo ? <div className="hd-nota" data-tono="mal" role="alert"><p>{fallo}</p></div> : <p className="hd-tenue">Mirando el documento…</p>);

  const momento = momentoDe(datos, pedido), toca = botonQueToca(momento);
  /** El relleno, para el botón del paso que toca y para ningún otro. */
  const boton = (cual: "pedir" | "confirmar" | "abrir") => `hd-boton${toca === cual ? " hd-principal" : ""}`;
  const t = datos.trabajo;
  const enMarchaAhora = momento === "en_curso" && t && (
    <div className="hd-nota" role="status">
      <p><b>{fraseDeAvance(t)}</b></p>
      <progress className="hd-avance" value={avanceDe(t)} max={1} aria-label="Avance del diagnóstico" />
      <p>{AVISO_EN_MARCHA}</p>
      <p className="hd-tenue">Se hace con el texto que estaba guardado cuando lo pediste. Los documentos van apareciendo en la carpeta del libro.</p>
    </div>
  );

  if (!datos.hay) {
    return marco(<>
      {enMarchaAhora}
      {momento === "freno" && <p>{porQueNo(datos)}</p>}
      {fallo && <div className="hd-nota" data-tono="mal" role="alert"><p>{fallo}</p></div>}
      {momento === "freno" && <div className="hd-botones"><button className="hd-boton" onClick={() => void mirar()}>Volver a mirar</button></div>}
    </>);
  }

  const d = datos, e = d.estimacion, freno = frenoDe(d);
  const hayQuePedir = d.pedidos.length > 0;
  // QUÉ OBRA ES: dos listas. Elegir guarda al momento, menos «otro», que espera a que lo describas.
  const o = d.obra, generoElegido = otro !== null ? "otro" : o?.genero?.id ?? "";
  const laObra = o && momento !== "en_curso" && (
    <div className="hd-obra">
      <label>Tipo de obra
        <select value={o.tipo} disabled={ocupado} onChange={(ev) => decirObra({ tipo: ev.target.value, genero: llevaGenero(o, ev.target.value) ? (o.genero && o.genero.id !== "otro" ? o.genero.id : null) : null })}>
          {o.tipos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
        </select>
      </label>
      {o.lleva_genero && (
        <label>Género
          <select value={generoElegido} disabled={ocupado} onChange={(ev) => (ev.target.value === "otro" ? setOtro(o.genero?.id === "otro" ? o.genero.descripcion ?? "" : "") : decirObra({ tipo: o.tipo, genero: ev.target.value || null }))}>
            <option value="">{SIN_GENERO}</option>
            {o.generos.map((g) => <option key={g.id} value={g.id}>{g.nombre}</option>)}
          </select>
        </label>
      )}
      {o.lleva_genero && generoElegido === "otro" && (otro !== null ? (
        <div className="hd-obra-otro">
          <input value={otro} maxLength={100} aria-label="Describe el género en pocas palabras" placeholder="Dilo en pocas palabras" onChange={(ev) => setOtro(ev.target.value)} />
          <button className="hd-boton" disabled={ocupado || !otro.trim()} onClick={() => decirObra({ tipo: o.tipo, genero: "otro", otro })}>Guardar</button>
        </div>
      ) : <button className="hd-boton" disabled={ocupado} onClick={() => setOtro(o.genero?.descripcion ?? "")}>Cambiar la descripción</button>)}
      <p className="hd-tenue">{fraseDeLaObra(o)}</p>
      {fraseDeObraCambiada(d) && <div className="hd-nota"><p>{fraseDeObraCambiada(d)}</p></div>}
    </div>
  );
  return marco(<>
    {momento === "nada" && <p>{fraseDeQueEs(d)}</p>}
    {momento === "nada" && fraseDeMismoTexto(d) && <div className="hd-nota"><p>{fraseDeMismoTexto(d)}</p></div>}
    {enMarchaAhora}
    {(momento === "hecho" || (momento === "estimado" && d.resultados.diagnostico)) && (
      <div className="hd-nota" data-tono="bien" role="status">
        <p>{fraseDeHecho(d)}</p>
        {d.texto_cambiado && <p>{FRASE_DE_TEXTO_CAMBIADO}</p>}
        {fraseDeMismoTexto(d) && <p>{fraseDeMismoTexto(d)}</p>}
      </div>
    )}
    {momento === "a_medias" && <div className="hd-nota" data-tono="mal" role="alert"><p>{fraseDeAMedias(d)}</p></div>}
    {fallo && <div className="hd-nota" data-tono="mal" role="alert"><p>{fallo}</p></div>}
    {freno && momento !== "en_curso" && hayQuePedir && <div className="hd-nota"><p>{freno}</p></div>}

    {momento === "hecho" && (
      <div className="hd-botones">
        <button className={boton("abrir")} disabled={ocupado || !d.resultados.diagnostico} onClick={() => abrir("diagnostico")}>Abrir el diagnóstico</button>
        <button className="hd-boton" disabled={ocupado || !d.resultados.plan} onClick={() => abrir("plan")}>Abrir el plan de reescritura</button>
      </div>
    )}
    {(momento === "nada" || momento === "a_medias" || (momento === "hecho" && hayQuePedir)) && (
      <div className="hd-botones">
        <button className={boton("pedir")} disabled={ocupado || !!freno} onClick={verLaCuenta}>{rotuloDePedir(d)}</button>
      </div>
    )}

    {momento !== "estimado" && laObra}

    {momento === "estimado" && (
      <div className="hd-cuenta">
        {hayQuePedir ? (<>
          <p className="hd-aviso" role="note">{AVISO_DE_DURACION}</p>
          {avisoDeMismoTexto(d) && <div className="hd-nota"><p>{avisoDeMismoTexto(d)}</p></div>}
          {avisoDePorEscenas(d) && <div className="hd-nota"><p>{avisoDePorEscenas(d)}</p></div>}
          {o && <p>{fraseDeLaObra(o)}</p>}
          {fraseDeObraCambiada(d) && <div className="hd-nota"><p>{fraseDeObraCambiada(d)}</p></div>}
          <ol className="hd-lista">
            {listaDeInformes(d).map((p) => <li key={p.id}>{p.titulo}{p.hecho && <span className="hd-tenue"> · ya está hecho, no se repite</span>}</li>)}
          </ol>
          <p>Y un Word que los junta: el diagnóstico. Se hace con «{d.fichero}» tal como está guardado ({miles(d.palabras)} palabras); tu manuscrito no se toca.</p>
          {fraseDelTiempo(e) && <p>{fraseDelTiempo(e)}</p>}
          {fraseDeLoQueViaja(e) && <p>{fraseDeLoQueViaja(e)}</p>}
          <p>{fraseDelDinero(e)}</p>
          {fraseDelPlanVigente(d) && <p>{fraseDelPlanVigente(d)}</p>}
        </>) : <p>{FRASE_SIN_GASTO}</p>}
        <div className="hd-botones">
          <button className={boton("confirmar")} disabled={ocupado || (hayQuePedir && !!freno)} onClick={confirmar}>{rotuloDeConfirmar(d)}</button>
          <button className="hd-boton" disabled={ocupado} onClick={() => setPedido(false)}>No, ahora no</button>
        </div>
      </div>
    )}
  </>);
}
